import cn from 'clsx';
import Image from 'next/image';
import React, { ReactNode } from 'react';

import { PaymentMethod } from '@/shared/api/wallet/generated/models';
import { DivElementType } from '@/shared/types/common';
import { Illustration, TagInfo } from '@/shared/ui';

import styles from './card-payment-method.module.scss';

export type CardPaymentMethodProps = DivElementType & {
  children?: ReactNode;
  isActive: boolean;
  toggleActive: () => void;
  paymentMethod: PaymentMethod;
};

const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    USD: '$',
    EUR: '€',
    USDT: '$',
  };

  return symbols[currency] || currency;
};

const formatFeeInfo = (paymentMethod: PaymentMethod): string => {
  const { percentFee, fixedFee, currency } = paymentMethod;
  const currencySymbol = getCurrencySymbol(currency);

  if (Number(fixedFee) !== 0) {
    return `${percentFee}% + ${fixedFee} ${currencySymbol}`;
  }
  return `${percentFee}%`;
};

export const CardPaymentMethodEntity = ({
  children,
  className,
  isActive,
  toggleActive,
  paymentMethod,
  ...props
}: CardPaymentMethodProps) => {
  const cardLiveFeedClasses = cn(
    styles['card-payment-method'],
    { [styles['active']]: isActive },
    className,
  );

  const feeInfo = formatFeeInfo(paymentMethod);

  return (
    <article onClick={toggleActive} className={cardLiveFeedClasses} {...props}>
      {Number(paymentMethod.fixedFee) !== 0 && (
        <TagInfo className={styles['tag-info']} variant={'discount-2'}>
          {feeInfo}
        </TagInfo>
      )}
      {Number(paymentMethod.percentFee) !== 0 && (
        <TagInfo className={styles['tag-info']} variant={'discount-2'}>
          {feeInfo}
        </TagInfo>
      )}
      <Image
        draggable={false}
        src={paymentMethod.imgUrl}
        width={88}
        height={42}
        quality={75}
        priority={true}
        loading="eager"
        alt={`${paymentMethod.provider} payment method`}
        className={styles['payment-method-image']}
      />
      {isActive && (
        <Illustration
          spanTagClassName={styles['check-icon']}
          name={'check-square'}
          spriteName={'icons'}
        />
      )}
    </article>
  );
};
